import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { RootState, useSelector } from "src/store";
import { useEnrollmentFlowEnabled } from "src/ui/containers/dashboard/enrollment/utils/useEnrollmentFlowEnabled";

import type { ListStatus } from "@dashboard/devices/store";
import { selectDevicesByGroupIds } from "@dashboard/devices/store/selectors/devices";
import LicenseSelectors from "@dashboard/licenses/store/selectors";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";

import { getFirstAvailableDashboardPath } from "./dashboardRouting";

const emptyGroupIds: number[] = [];

export function isDeviceListReadyForDefaultPath(
    enrollmentPageEnabled: boolean,
    hasWorkspace: boolean,
    groupsStatus: ListStatus | null,
    licensesStatus: ListStatus | null,
): boolean {
    if (!enrollmentPageEnabled || !hasWorkspace) {
        return true;
    }
    const groupsReady = groupsStatus === "fulfilled" || groupsStatus === "rejected";
    const licensesReady = licensesStatus === "fulfilled" || licensesStatus === "rejected";
    return groupsReady && licensesReady;
}

export function useDefaultDashboardPath(): { path: string; isLoading: boolean } {
    const {
        effectiveWorkspaceSettings,
        rawTenantSettings,
        isLoading: workspaceLoading,
        isFetching: workspaceFetching,
    } = useAppSettingsService();
    const { data: workspace } = useCurrentWorkspace();

    const groupIds = workspace?.subscription?.groupIds ?? emptyGroupIds;
    const devices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds));
    const groupsStatus = useSelector((state: RootState) => state.devices.list.groups.status);
    const licensesStatus = useSelector(LicenseSelectors.selectLicensesStatus);

    const isWorkspaceLoading = workspaceLoading || (workspaceFetching && workspace === null);
    const enrollmentPageEnabled = useEnrollmentFlowEnabled();
    const isDeviceListLoading = !isDeviceListReadyForDefaultPath(
        enrollmentPageEnabled,
        workspace !== undefined && workspace !== null,
        groupsStatus,
        licensesStatus,
    );

    const accessContext = { effective: effectiveWorkspaceSettings, pages: rawTenantSettings.features.pages };
    const defaultPath =
        !enrollmentPageEnabled || devices.length > 0
            ? (getFirstAvailableDashboardPath(accessContext) ?? "/forbidden")
            : "/dashboard/enrollment";

    return {
        path: defaultPath,
        isLoading: isWorkspaceLoading || isDeviceListLoading,
    };
}
